import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import React, { useEffect } from 'react';
import { Grid as VirtualGrid } from 'react-window';

import { selectionStore } from '../PdfSplitEditor/utils/SelectionStore';
import { DragOverlayContent } from './components/DragOverlayContent';
import type { CellPropsExtras } from './components/GridCellComponent';
import { GridCellComponent } from './components/GridCellComponent';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useGridScrollTracking } from './hooks/usePageScrollTracking';
import {
  getGroupBorderColor,
  getGroupColor,
  GROUP_COLORS,
} from './utils/groupColors';

interface PdfGridViewProps {
  pages?: number[];
  currentPage?: number;
  pageGroups?: number[][];
  onPageClick?: (page: number) => void;
  onReorderPages?: (newGroups: number[][]) => void;
  onSelectionChange?: (selectedPages: number[]) => void;
  onRemovePage?: (pageNum: number) => void;
}

const THUMBNAIL_WIDTH = 180; // width of each grid square in px
const THUMBNAIL_HEIGHT = 240; // height of each grid square in px
const COLUMN_COUNT = 4; // number of columns in grid
const OVERSCAN = 2;

const PdfGridView: React.FC<PdfGridViewProps> = ({
  currentPage,
  pageGroups = [],
  pages = [],
  onReorderPages,
  onPageClick,
  onSelectionChange,
  onRemovePage,
}) => {
  const { containerRef } = useGridScrollTracking({
    selectedPage: currentPage,
    columnCount: COLUMN_COUNT,
    onPageChange: onPageClick,
    totalPages: pages.length,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6, // prevents accidental drags
      },
    })
  );

  const { handleDragEnd } = useDragAndDrop({
    pageGroups,
    onReorderPages,
  });

  const pageGroupsKey = JSON.stringify(pageGroups);

  useEffect(() => {
    if (onReorderPages) {
      onReorderPages(pageGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageGroupsKey]);

  const handlePageClick = React.useCallback(
    (pageNumber: number, e: React.MouseEvent) => {
      if (!(e.ctrlKey || e.metaKey) && onPageClick) {
        onPageClick(pageNumber);
      }
      if ((e.ctrlKey || e.metaKey) && onSelectionChange) {
        onSelectionChange(selectionStore.getAll().sort((a, b) => a - b));
      }
    },
    [onPageClick, onSelectionChange]
  );

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <DragOverlayContent />
      <VirtualGrid
        gridRef={containerRef}
        columnCount={COLUMN_COUNT}
        rowCount={Math.ceil(pages.length / COLUMN_COUNT)}
        columnWidth={THUMBNAIL_WIDTH}
        rowHeight={THUMBNAIL_HEIGHT}
        overscanCount={OVERSCAN} // optional, renders 2 extra rows offscreen
        cellComponent={({ pages, ...props }) => {
          const pageIndex = props.rowIndex * COLUMN_COUNT + props.columnIndex;
          if (pageIndex >= pages.length) return <React.Fragment />;
          const pageNumber = pages[pageIndex];
          const isCurrentPage = pageNumber === currentPage;

          const groupIndex = pageGroups.findIndex((g) =>
            g.includes(pageNumber)
          );
          const isGrouped =
            groupIndex !== -1 && pageGroups[groupIndex].length > 1;
          const groupNumber = isGrouped
            ? pageGroups.slice(0, groupIndex).filter((g) => g.length > 1)
                .length + 1
            : 0;
          const positionInGroup = isGrouped
            ? pageGroups[groupIndex].indexOf(pageNumber) + 1
            : 0;
          const groupSize = isGrouped ? pageGroups[groupIndex].length : 1;
          const colorIndex = isGrouped
            ? Math.min(...pageGroups[groupIndex]) % GROUP_COLORS.length
            : 0;
          const groupColor = getGroupColor(colorIndex, isGrouped);
          const groupBorderColor = getGroupBorderColor(colorIndex, isGrouped);

          const pageProps: CellPropsExtras = {
            pageNumber,
            isCurrentPage,
            isGrouped,
            groupNumber,
            positionInGroup,
            groupSize,
            groupColor,
            groupBorderColor,
            onClick: handlePageClick,
            onRemovePage,
          };
          return <GridCellComponent {...props} {...pageProps} />;
        }}
        cellProps={{ pages }}
      />
    </DndContext>
  );
};

export default React.memo(PdfGridView, (prev, next) => {
  return (
    JSON.stringify(prev.pages) === JSON.stringify(next.pages) &&
    JSON.stringify(prev.pageGroups) === JSON.stringify(next.pageGroups) &&
    prev.currentPage === next.currentPage
  );
});
